<script>
    import { onMount } from 'svelte';
    import {
        fetchReportsOverview,
        fetchSalesByHour,
        fetchTopProducts,
        fetchPaymentMethods,
        getCurrentStore
    } from '../services/api';

    export let onNotify = (msg, type) => {};

    const store = getCurrentStore() || { id: '', name: 'Restaurante' };

    let selectedPeriod = 'all'; // 'today', '7days', '30days', 'all'
    let isLoading = true;

    // Dados de Relatórios & DRE Gerencial
    let kpis = {
        totalRevenue: 0,
        totalOrders: 0,
        completedOrders: 0,
        averageTicket: 0,
        successRate: 100
    };
    let dre = {
        grossSales: 0,
        deliveryFees: 0,
        discounts: 0,
        netRevenue: 0,
        estimatedCMV: 0,
        estimatedGrossMargin: 0
    };
    let hourlySales = [];
    let topProducts = [];
    let paymentMethods = [];

    onMount(async () => {
        await loadReports();
    });

    async function loadReports() {
        isLoading = true;
        try {
            const [overviewData, hourData, productsData, paymentData] = await Promise.all([
                fetchReportsOverview(selectedPeriod),
                fetchSalesByHour(selectedPeriod),
                fetchTopProducts(selectedPeriod),
                fetchPaymentMethods(selectedPeriod)
            ]);

            if (overviewData) {
                kpis = overviewData.kpis || kpis;
                dre = overviewData.dre || dre;
            }
            if (hourData && hourData.hours) {
                hourlySales = hourData.hours;
            }
            if (productsData && productsData.products) {
                topProducts = productsData.products;
            }
            if (paymentData && paymentData.methods) {
                paymentMethods = paymentData.methods;
            }
        } catch (err) {
            console.warn('Erro ao buscar relatórios:', err);
            onNotify('Erro ao sincronizar relatórios financeiros.', 'error');
        } finally {
            isLoading = false;
        }
    }

    function setPeriod(p) {
        selectedPeriod = p;
        loadReports();
    }

    function formatBRL(val) {
        return 'R$ ' + parseFloat(val || 0).toFixed(2).replace('.', ',');
    }

    $: maxHourCount = Math.max(...hourlySales.map(h => h.count), 1);
</script>

<div class="reports-container">
    <!-- Cabeçalho Executivo do Proprietário -->
    <header class="view-header">
        <div class="header-left">
            <div class="header-badge-row">
                <span class="module-badge">FINANCEIRO & GESTÃO</span>
                <span class="owner-pill">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        <polyline points="9 12 11 14 15 10"></polyline>
                    </svg>
                    Painel Exclusivo da Gestão
                </span>
            </div>
            <div class="title-wrapper">
                <h1 class="view-title">Financeiro & DRE</h1>
                <p class="view-subtitle">Demonstrativo de Resultados do Exercício (DRE), margens operacionais, curva de vendas e faturamento consolidado.</p>
            </div>
        </div>

        <div class="header-actions">
            <button
                type="button"
                class="btn-action btn-print"
                on:click={() => window.print()}
                title="Imprimir relatório analítico em papel ou PDF"
            >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                </svg>
                <span>Imprimir DRE</span>
            </button>

            <button
                type="button"
                class="btn-action btn-refresh"
                class:loading={isLoading}
                on:click={loadReports}
                disabled={isLoading}
                title="Sincronizar indicadores em tempo real"
            >
                <svg class="refresh-icon" class:spin={isLoading} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
                <span>{isLoading ? 'Sincronizando...' : 'Atualizar'}</span>
            </button>
        </div>
    </header>

    <div class="view-content">
        <!-- Barra de Filtros de Período -->
        <div class="filter-bar">
            <div class="period-control-group">
                <span class="filter-label">Período de Análise:</span>
                <div class="segmented-control" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={selectedPeriod === 'today'}
                        class="seg-btn {selectedPeriod === 'today' ? 'active' : ''}"
                        on:click={() => setPeriod('today')}
                    >
                        Hoje
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={selectedPeriod === '7days'}
                        class="seg-btn {selectedPeriod === '7days' ? 'active' : ''}"
                        on:click={() => setPeriod('7days')}
                    >
                        7 Dias
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={selectedPeriod === '30days'}
                        class="seg-btn {selectedPeriod === '30days' ? 'active' : ''}"
                        on:click={() => setPeriod('30days')}
                    >
                        30 Dias
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={selectedPeriod === 'all'}
                        class="seg-btn {selectedPeriod === 'all' ? 'active' : ''}"
                        on:click={() => setPeriod('all')}
                    >
                        Geral (Histórico)
                    </button>
                </div>
            </div>

            {#if store?.name}
                <div class="store-badge">
                    <span class="store-dot"></span>
                    <span class="store-name">{store.name}</span>
                </div>
            {/if}
        </div>

        <!-- Grid de KPIs Cards -->
        <div class="kpis-grid">
            <!-- Card 1: Faturamento Líquido -->
            <div class="kpi-card kpi-emerald">
                <div class="kpi-header">
                    <span class="kpi-label">Faturamento Total</span>
                    <span class="kpi-tag-pill green">Líquido</span>
                </div>
                <div class="kpi-value">{formatBRL(kpis.totalRevenue)}</div>
                <div class="kpi-footer">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                        <polyline points="17 6 23 6 23 12"></polyline>
                    </svg>
                    <span>Receita consolidada de pedidos</span>
                </div>
            </div>

            <!-- Card 2: Pedidos Concluídos -->
            <div class="kpi-card kpi-blue">
                <div class="kpi-header">
                    <span class="kpi-label">Pedidos Concluídos</span>
                    <span class="kpi-icon-pill blue">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                    </span>
                </div>
                <div class="kpi-value">{kpis.completedOrders} <span class="unit">pedidos</span></div>
                <div class="kpi-footer">
                    <span>Total de {kpis.totalOrders} pedidos recebidos</span>
                </div>
            </div>

            <!-- Card 3: Ticket Médio -->
            <div class="kpi-card kpi-purple">
                <div class="kpi-header">
                    <span class="kpi-label">Ticket Médio</span>
                    <span class="kpi-icon-pill purple">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path>
                            <path d="M12 18V6"></path>
                        </svg>
                    </span>
                </div>
                <div class="kpi-value">{formatBRL(kpis.averageTicket)}</div>
                <div class="kpi-footer">
                    <span>Média consumida por pedido</span>
                </div>
            </div>

            <!-- Card 4: Taxa de Conclusão -->
            <div class="kpi-card kpi-amber">
                <div class="kpi-header">
                    <span class="kpi-label">Taxa de Conversão</span>
                    <span class="kpi-icon-pill amber">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                    </span>
                </div>
                <div class="kpi-value">{kpis.successRate}%</div>
                <div class="kpi-footer">
                    <span>{kpis.totalOrders - kpis.completedOrders} cancelamento(s)</span>
                </div>
            </div>
        </div>

        <!-- Linha Principal: DRE Simplificado e Formas de Pagamento -->
        <div class="reports-main-grid">
            <!-- Card 1: DRE Simplificado -->
            <section class="card dre-card">
                <div class="card-header">
                    <div class="card-title">
                        <div class="card-icon-box orange">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                        </div>
                        <div>
                            <h3>Demonstrativo de Resultado (DRE Simplificado)</h3>
                            <span class="card-desc">Visão consolidada da operação de vendas</span>
                        </div>
                    </div>
                    <span class="badge-status">Gerencial</span>
                </div>

                <div class="dre-table">
                    <div class="dre-row">
                        <div class="dre-item">
                            <span class="math-op plus">(+)</span>
                            <span>Receita Bruta dos Produtos</span>
                        </div>
                        <span class="dre-val val-positive">{formatBRL(dre.grossSales)}</span>
                    </div>

                    <div class="dre-row">
                        <div class="dre-item">
                            <span class="math-op plus">(+)</span>
                            <span>Taxas de Entrega Recebidas</span>
                        </div>
                        <span class="dre-val val-positive">+ {formatBRL(dre.deliveryFees)}</span>
                    </div>

                    <div class="dre-row">
                        <div class="dre-item">
                            <span class="math-op minus">(-)</span>
                            <span>Descontos & Cupons Concedidos</span>
                        </div>
                        <span class="dre-val val-negative">- {formatBRL(dre.discounts)}</span>
                    </div>

                    <div class="dre-divider"></div>

                    <div class="dre-row row-net-revenue">
                        <div class="dre-item">
                            <span class="math-op equal">(=)</span>
                            <span class="bold-text">FATURAMENTO LÍQUIDO TOTAL</span>
                        </div>
                        <span class="dre-val val-net-total">{formatBRL(dre.netRevenue)}</span>
                    </div>

                    <div class="dre-row row-cmv">
                        <div class="dre-item">
                            <span class="math-op minus">(-)</span>
                            <span>Custo Estimado de Insumos (CMV ~32%)</span>
                        </div>
                        <span class="dre-val val-cmv">- {formatBRL(dre.estimatedCMV)}</span>
                    </div>

                    <div class="dre-row row-gross-margin">
                        <div class="margin-label-group">
                            <span class="math-op equal green">(=)</span>
                            <div>
                                <span class="margin-title">Margem de Contribuição Bruta</span>
                                <span class="margin-subtitle">Receita líquida após dedução estimada de insumos</span>
                            </div>
                        </div>
                        <span class="dre-val val-margin">{formatBRL(dre.estimatedGrossMargin)}</span>
                    </div>

                    <div class="dre-disclaimer">
                        <span class="dre-disclaimer-text">
                            * O CMV apresentado é uma estimativa baseada no percentual configurado (~32%). O sistema ainda não calcula o custo real dos insumos por ficha técnica.
                        </span>
                    </div>
                </div>
            </section>

            <!-- Card 2: Formas de Pagamento -->
            <section class="card payment-card">
                <div class="card-header">
                    <div class="card-title">
                        <div class="card-icon-box green">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                <line x1="1" y1="10" x2="23" y2="10"></line>
                            </svg>
                        </div>
                        <div>
                            <h3>Formas de Pagamento</h3>
                            <span class="card-desc">Distribuição financeira por meio de quitação</span>
                        </div>
                    </div>
                    <span class="badge-total">Total: {formatBRL(kpis.totalRevenue)}</span>
                </div>

                <div class="payment-list">
                    {#if paymentMethods.length === 0}
                        <div class="empty-state-box">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                            <p class="empty-text">Nenhuma venda registrada no período selecionado.</p>
                        </div>
                    {:else}
                        {#each paymentMethods as pm}
                            <div class="payment-row">
                                <div class="pm-header-row">
                                    <div class="pm-name">
                                        {#if pm.method === 'PIX'}
                                            <span class="pm-dot pix"></span>
                                            <span class="pm-title">PIX</span>
                                        {:else if pm.method === 'Dinheiro'}
                                            <span class="pm-dot cash"></span>
                                            <span class="pm-title">Dinheiro</span>
                                        {:else}
                                            <span class="pm-dot card-method"></span>
                                            <span class="pm-title">{pm.method}</span>
                                        {/if}
                                        <span class="pm-count-badge">{pm.count}x</span>
                                    </div>
                                    <div class="pm-amount-wrapper">
                                        <span class="pm-amount">{formatBRL(pm.amount)}</span>
                                        <span class="pm-percent">{pm.percent}%</span>
                                    </div>
                                </div>
                                <div class="progress-bar-track">
                                    <div
                                        class="progress-bar-fill"
                                        class:fill-pix={pm.method === 'PIX'}
                                        class:fill-cash={pm.method === 'Dinheiro'}
                                        class:fill-card={pm.method !== 'PIX' && pm.method !== 'Dinheiro'}
                                        style="width: {pm.percent}%;"
                                    ></div>
                                </div>
                            </div>
                        {/each}
                    {/if}
                </div>
            </section>
        </div>

        <!-- Segunda Linha: Horários de Pico e Top Produtos -->
        <div class="reports-secondary-grid">
            <!-- Card 3: Horários de Pico -->
            <section class="card hour-card">
                <div class="card-header">
                    <div class="card-title">
                        <div class="card-icon-box blue">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                        </div>
                        <div>
                            <h3>Horários de Pico (Volume de Vendas)</h3>
                            <span class="card-desc">Concentração de pedidos e receita ao longo do dia</span>
                        </div>
                    </div>
                    <span class="tag-hint">Demanda por hora</span>
                </div>

                <div class="hour-chart-wrapper">
                    {#if hourlySales.length === 0}
                        <div class="empty-state-box">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            <p class="empty-text">Sem dados suficientes para o período.</p>
                        </div>
                    {:else}
                        <div class="bars-container">
                            {#each hourlySales as hs}
                                <div class="chart-col">
                                    <div class="bar-wrapper" title="{hs.hour}: {hs.count} pedidos ({formatBRL(hs.revenue)})">
                                        <div
                                            class="bar-fill"
                                            class:has-sales={hs.count > 0}
                                            style="height: {(hs.count / maxHourCount) * 100}%;"
                                        >
                                            {#if hs.count > 0}
                                                <span class="bar-qty">{hs.count}</span>
                                            {/if}
                                        </div>
                                    </div>
                                    <span class="col-label">{hs.hour}</span>
                                </div>
                            {/each}
                        </div>
                    {/if}
                </div>
            </section>

            <!-- Card 4: Top Produtos Mais Vendidos -->
            <section class="card top-card">
                <div class="card-header">
                    <div class="card-title">
                        <div class="card-icon-box purple">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                            </svg>
                        </div>
                        <div>
                            <h3>Top Produtos Mais Vendidos</h3>
                            <span class="card-desc">Itens campeões em volume e receita faturada</span>
                        </div>
                    </div>
                    <span class="tag-hint">Ranking</span>
                </div>

                <div class="table-responsive">
                    <table class="top-table">
                        <thead>
                            <tr>
                                <th class="col-rank">#</th>
                                <th class="col-product">Produto</th>
                                <th class="col-qty">Qtd</th>
                                <th class="col-rev">Receita</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#if topProducts.length === 0}
                                <tr>
                                    <td colspan="4">
                                        <div class="empty-state-table">
                                            <p class="empty-text">Nenhum produto vendido no período.</p>
                                        </div>
                                    </td>
                                </tr>
                            {:else}
                                {#each topProducts as p}
                                    <tr class="product-row">
                                        <td class="rank-td">
                                            {#if p.rank === 1}
                                                <span class="medal-badge gold" title="1º Lugar">🥇</span>
                                            {:else if p.rank === 2}
                                                <span class="medal-badge silver" title="2º Lugar">🥈</span>
                                            {:else if p.rank === 3}
                                                <span class="medal-badge bronze" title="3º Lugar">🥉</span>
                                            {:else}
                                                <span class="rank-num">{p.rank}</span>
                                            {/if}
                                        </td>
                                        <td class="prod-name-td">
                                            <span class="prod-name-text">{p.name}</span>
                                        </td>
                                        <td class="qty-td">
                                            <span class="qty-pill">{p.quantity}x</span>
                                        </td>
                                        <td class="rev-td">
                                            <span class="rev-text">{formatBRL(p.revenue)}</span>
                                        </td>
                                    </tr>
                                {/each}
                            {/if}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    </div>
</div>

<style>
    /* Container Principal */
    .reports-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        background: var(--color-neutral-50, #F8FAFC);
        overflow-y: auto;
        padding: 24px 32px 48px;
        color: var(--color-neutral-800, #1E293B);
        box-sizing: border-box;
    }

    /* 1. Header */
    .view-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 24px;
        flex-wrap: wrap;
        gap: 16px;
    }

    .header-left {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .header-badge-row {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .module-badge {
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.6px;
        background: #FFF7ED;
        color: #C2410C;
        border: 1px solid #FFEDD5;
        padding: 2px 8px;
        border-radius: 6px;
    }

    .owner-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11px;
        font-weight: 700;
        background: #FEF3C7;
        color: #92400E;
        border: 1px solid #FDE68A;
        padding: 2px 8px;
        border-radius: 6px;
    }

    .title-wrapper {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .view-title {
        font-size: 24px;
        font-weight: 800;
        color: var(--color-neutral-900, #0F172A);
        letter-spacing: -0.5px;
        margin: 0;
    }

    .view-subtitle {
        font-size: 13px;
        color: var(--color-neutral-500, #64748B);
        margin: 0;
        line-height: 1.4;
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .btn-action {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 9px 16px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.18s ease;
        border: 1px solid transparent;
    }

    .btn-print {
        background: #FFFFFF;
        border-color: #E2E8F0;
        color: #334155;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    }

    .btn-print:hover {
        background: #F8FAFC;
        border-color: #CBD5E1;
        color: #0F172A;
    }

    .btn-refresh {
        background: #0F172A;
        color: #FFFFFF;
        box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
    }

    .btn-refresh:hover:not(:disabled) {
        background: #1E293B;
    }

    .btn-refresh:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .refresh-icon.spin {
        animation: spin-rotate 0.9s linear infinite;
    }

    @keyframes spin-rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    /* 2. Barra de Filtro de Período */
    .filter-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;
        flex-wrap: wrap;
        gap: 14px;
    }

    .period-control-group {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }

    .filter-label {
        font-size: 12px;
        font-weight: 700;
        color: #64748B;
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }

    .segmented-control {
        display: inline-flex;
        align-items: center;
        background: #FFFFFF;
        padding: 4px;
        border-radius: var(--radius-md, 10px);
        border: 1px solid #E2E8F0;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    }

    .seg-btn {
        background: transparent;
        border: none;
        padding: 7px 14px;
        border-radius: 7px;
        font-size: 12px;
        font-weight: 700;
        color: #64748B;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .seg-btn:hover:not(.active) {
        color: #0F172A;
        background: #F1F5F9;
    }

    .seg-btn.active {
        background: #0F172A;
        color: #FFFFFF;
        box-shadow: 0 1px 3px rgba(15, 23, 42, 0.15);
    }

    .store-badge {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        color: #475569;
    }

    .store-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #10B981;
    }

    /* 3. Grid de KPIs */
    .kpis-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
        margin-bottom: 24px;
    }

    @media (max-width: 1100px) {
        .kpis-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (max-width: 600px) {
        .kpis-grid {
            grid-template-columns: 1fr;
        }
    }

    .kpi-card {
        background: #FFFFFF;
        border-radius: var(--radius-lg, 14px);
        padding: 18px 20px;
        border: 1px solid #E2E8F0;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        position: relative;
        overflow: hidden;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
    }

    .kpi-card:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .kpi-card::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 4px;
    }

    .kpi-card.kpi-emerald::before { background: #10B981; }
    .kpi-card.kpi-blue::before { background: #3B82F6; }
    .kpi-card.kpi-purple::before { background: #8B5CF6; }
    .kpi-card.kpi-amber::before { background: #F59E0B; }

    .kpi-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
    }

    .kpi-label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #64748B;
    }

    .kpi-tag-pill {
        font-size: 10px;
        font-weight: 800;
        padding: 2px 7px;
        border-radius: 4px;
        letter-spacing: 0.3px;
    }

    .kpi-tag-pill.green {
        background: #ECFDF5;
        color: #059669;
        border: 1px solid #A7F3D0;
    }

    .kpi-icon-pill {
        width: 24px;
        height: 24px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .kpi-icon-pill.blue { background: #EFF6FF; color: #2563EB; }
    .kpi-icon-pill.purple { background: #F5F3FF; color: #7C3AED; }
    .kpi-icon-pill.amber { background: #FFFBEB; color: #D97706; }

    .kpi-value {
        font-size: 26px;
        font-weight: 800;
        color: #0F172A;
        letter-spacing: -0.5px;
        margin-bottom: 8px;
        line-height: 1.1;
    }

    .kpi-value .unit {
        font-size: 14px;
        font-weight: 600;
        color: #64748B;
    }

    .kpi-footer {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 12px;
        color: #94A3B8;
        font-weight: 500;
    }

    /* 4. Grids de Conteúdo */
    .reports-main-grid,
    .reports-secondary-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        margin-bottom: 24px;
    }

    @media (max-width: 980px) {
        .reports-main-grid,
        .reports-secondary-grid {
            grid-template-columns: 1fr;
        }
    }

    .card {
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: var(--radius-lg, 14px);
        padding: 22px 24px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        gap: 12px;
    }

    .card-title {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .card-icon-box {
        width: 38px;
        height: 38px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .card-icon-box.orange { background: #FFF7ED; color: #EA580C; }
    .card-icon-box.green { background: #ECFDF5; color: #059669; }
    .card-icon-box.blue { background: #EFF6FF; color: #2563EB; }
    .card-icon-box.purple { background: #F5F3FF; color: #7C3AED; }

    .card-title h3 {
        margin: 0;
        font-size: 15px;
        font-weight: 800;
        color: #0F172A;
        letter-spacing: -0.2px;
    }

    .card-desc {
        display: block;
        font-size: 12px;
        color: #64748B;
        margin-top: 2px;
    }

    .badge-status {
        background: #F1F5F9;
        color: #475569;
        font-size: 11px;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 6px;
    }

    .badge-total {
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        color: #334155;
        font-size: 12px;
        font-weight: 700;
        padding: 4px 10px;
        border-radius: 8px;
    }

    .tag-hint {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: #94A3B8;
    }

    /* 5. DRE Estruturado */
    .dre-table {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .dre-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 10px;
        border-radius: 8px;
        font-size: 13px;
        transition: background 0.12s ease;
    }

    .dre-row:hover {
        background: #F8FAFC;
    }

    .dre-item {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #334155;
        font-weight: 500;
    }

    .math-op {
        font-family: monospace;
        font-weight: 800;
        font-size: 13px;
        width: 20px;
        display: inline-block;
    }

    .math-op.plus { color: #10B981; }
    .math-op.minus { color: #EF4444; }
    .math-op.equal { color: #0F172A; }
    .math-op.equal.green { color: #059669; }

    .dre-val {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        font-size: 13px;
    }

    .val-positive { color: #059669; }
    .val-negative { color: #DC2626; }
    .val-cmv { color: #64748B; font-weight: 600; }

    .dre-divider {
        height: 1px;
        border-bottom: 1px dashed #CBD5E1;
        margin: 6px 0;
    }

    .dre-row.row-net-revenue {
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        padding: 10px 12px;
    }

    .bold-text {
        font-weight: 800;
        color: #0F172A;
        letter-spacing: 0.2px;
    }

    .val-net-total {
        font-size: 15px;
        font-weight: 800;
        color: #0F172A;
    }

    .row-cmv {
        font-style: italic;
        color: #64748B;
    }

    .row-gross-margin {
        background: #F0FDF4;
        border: 1px solid #BBF7D0;
        padding: 12px 14px;
        border-radius: 10px;
        margin-top: 4px;
    }

    .margin-label-group {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .margin-title {
        font-size: 13px;
        font-weight: 800;
        color: #166534;
        display: block;
    }

    .margin-subtitle {
        font-size: 11px;
        color: #15803D;
        opacity: 0.85;
    }

    .val-margin {
        font-size: 16px;
        font-weight: 800;
        color: #15803D;
    }

    .dre-disclaimer {
        margin-top: 10px;
        padding: 8px 12px;
        background: #F8FAFC;
        border: 1px dashed #CBD5E1;
        border-radius: 8px;
    }

    .dre-disclaimer-text {
        font-size: 11px;
        color: #64748B;
        line-height: 1.4;
        display: block;
    }

    /* 6. Formas de Pagamento */
    .payment-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .payment-row {
        display: flex;
        flex-direction: column;
        gap: 7px;
    }

    .pm-header-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .pm-name {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .pm-dot {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    .pm-dot.pix { background: #10B981; }
    .pm-dot.cash { background: #F59E0B; }
    .pm-dot.card-method { background: #3B82F6; }

    .pm-title {
        font-size: 13px;
        font-weight: 700;
        color: #0F172A;
    }

    .pm-count-badge {
        font-size: 10px;
        font-weight: 700;
        background: #F1F5F9;
        color: #64748B;
        padding: 1px 6px;
        border-radius: 4px;
    }

    .pm-amount-wrapper {
        display: flex;
        align-items: baseline;
        gap: 6px;
    }

    .pm-amount {
        font-size: 13px;
        font-weight: 700;
        color: #0F172A;
        font-variant-numeric: tabular-nums;
    }

    .pm-percent {
        font-size: 12px;
        font-weight: 600;
        color: #64748B;
    }

    .progress-bar-track {
        width: 100%;
        height: 7px;
        background: #F1F5F9;
        border-radius: 4px;
        overflow: hidden;
    }

    .progress-bar-fill {
        height: 100%;
        border-radius: 4px;
        transition: width 0.4s ease;
    }

    .fill-pix { background: #10B981; }
    .fill-cash { background: #F59E0B; }
    .fill-card { background: #3B82F6; }

    /* 7. Gráfico de Horários */
    .hour-chart-wrapper {
        height: 200px;
        display: flex;
        align-items: flex-end;
        padding-top: 10px;
    }

    .bars-container {
        display: flex;
        width: 100%;
        height: 170px;
        align-items: flex-end;
        gap: 6px;
    }

    .chart-col {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;
        justify-content: flex-end;
    }

    .bar-wrapper {
        width: 100%;
        height: 140px;
        display: flex;
        align-items: flex-end;
        justify-content: center;
    }

    .bar-fill {
        width: 75%;
        min-width: 8px;
        background: #E2E8F0;
        border-radius: 4px 4px 0 0;
        position: relative;
        display: flex;
        justify-content: center;
        transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.2s ease;
    }

    .bar-fill.has-sales {
        background: #0F172A;
    }

    .bar-fill.has-sales:hover {
        background: #EA580C;
    }

    .bar-qty {
        position: absolute;
        top: -18px;
        font-size: 10px;
        font-weight: 800;
        color: #0F172A;
    }

    .col-label {
        font-size: 10px;
        color: #64748B;
        margin-top: 6px;
        font-weight: 700;
    }

    /* 8. Tabela de Top Produtos */
    .table-responsive {
        width: 100%;
        overflow-x: auto;
    }

    .top-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }

    .top-table th {
        text-align: left;
        color: #64748B;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding-bottom: 10px;
        border-bottom: 1px solid #E2E8F0;
    }

    .col-rank { width: 44px; }
    .col-product { text-align: left; }
    .col-qty { width: 70px; text-align: center; }
    .col-rev { width: 110px; text-align: right; }

    .top-table td {
        padding: 10px 0;
        border-bottom: 1px solid #F1F5F9;
        vertical-align: middle;
    }

    .product-row:hover td {
        background: #F8FAFC;
    }

    .rank-td {
        text-align: left;
    }

    .medal-badge {
        font-size: 15px;
        line-height: 1;
        display: inline-block;
    }

    .rank-num {
        font-size: 12px;
        font-weight: 800;
        color: #64748B;
        background: #F1F5F9;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .prod-name-td {
        font-weight: 700;
        color: #0F172A;
    }

    .prod-name-text {
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .qty-td {
        text-align: center;
    }

    .qty-pill {
        font-size: 12px;
        font-weight: 700;
        color: #334155;
        background: #F1F5F9;
        padding: 2px 8px;
        border-radius: 6px;
    }

    .rev-td {
        text-align: right;
    }

    .rev-text {
        font-weight: 800;
        color: #0F172A;
        font-variant-numeric: tabular-nums;
    }

    /* Estados Vazios */
    .empty-state-box {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 32px 16px;
        text-align: center;
        gap: 8px;
    }

    .empty-state-table {
        padding: 24px 0;
        text-align: center;
    }

    .empty-text {
        color: #94A3B8;
        font-size: 13px;
        margin: 0;
        font-weight: 500;
    }

    /* 9. Estilos de Impressão */
    @media print {
        .reports-container {
            background: #FFFFFF !important;
            padding: 0 !important;
            overflow: visible !important;
        }

        .header-actions,
        .filter-bar,
        .kpi-footer {
            display: none !important;
        }

        .card {
            border: 1px solid #CBD5E1 !important;
            box-shadow: none !important;
            break-inside: avoid;
        }

        .reports-main-grid,
        .reports-secondary-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
        }

        .kpis-grid {
            grid-template-columns: repeat(4, 1fr) !important;
        }
    }
</style>
